import React, {
    useState,
    HTMLAttributes,
    ReactElement,
    SyntheticEvent,
    KeyboardEvent
} from 'react';
import classNames from 'classnames';

import Svg from '../svg/svg';
import search from 'assets/icons/search.svg';
import './search.scss';

interface SearchItemInterface {
    title: string;
    value: string;
    checked?: boolean;
}

interface SearchProps extends HTMLAttributes<HTMLInputElement> {
    name: string;
    url: string;
    value?: string;
    list?: SearchItemInterface[];
    readOnly?: boolean;
    onQueryChange?: (value: string) => void;
    onSubmit?: () => void;
}

const Search: React.FC<SearchProps> = (
    {
        defaultValue,
        placeholder = 'Szukaj',
        list,
        readOnly = false,
        onQueryChange,
        onSubmit,
        className
    }
): ReactElement => {
    const initList = list?.filter(({ value }): string => value).map(({ title, value }): SearchItemInterface => ({ value, title: title || value }));
    const [active, setActive] = useState(false);
    const [itemList, setItemList] = useState(initList);
    const [defaultItem, setDefaultItem] = useState(itemList?.find(({ value }): boolean => defaultValue === value) || ({
        value: '',
        title: ''
    }));
    const [searchValue, setSearchValue] = useState(defaultValue || defaultItem?.title || defaultItem?.value);
    const searchClasses = classNames(className, 'search');
    const searchListClasses = classNames('search__list', { 'search__list--active': active });

    const handleFocus = (): void => {
        setActive(true);
    };

    const handleBlur = (): void => {
        setTimeout(() => {
            setActive(false);
            setItemList(initList);
            setDefaultItem(prevDefaultItem => {
                setSearchValue(prevDefaultItem?.title || prevDefaultItem?.value);

                return prevDefaultItem;
            });
        }, 200);
    };

    /* TODO implement async search request */
    const handleSearch = ({ target }: SyntheticEvent): void => {
        const { value } = target as HTMLInputElement;

        setSearchValue(value);
        setItemList(value ? initList?.filter((item): boolean => item.title.toLowerCase().includes(value.toLowerCase())) : initList);

        if (typeof onQueryChange !== 'undefined') {
            onQueryChange(value);
        }
    };

    const handleChange = (item: SearchItemInterface): void => {
        setSearchValue(item.title || item.value);
        setDefaultItem(item);
        setItemList(itemList?.map((listItem): SearchItemInterface => listItem.value === defaultItem?.value
            ? { ...listItem, checked: true }
            : { ...listItem, checked: false }));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        if (typeof onSubmit !== 'undefined' && e.key === 'Enter') {
            onSubmit();
        }
    };

    return (
        <div className={searchClasses} onFocus={handleFocus} onBlur={handleBlur}
            role="button"
        >
            <div className="search__input-wrapper">
                <input
                    className="search__input"
                    type="text"
                    readOnly={readOnly}
                    value={searchValue}
                    placeholder={placeholder}
                    onChange={handleSearch}
                    onKeyDown={handleKeyDown}
                    aria-label={placeholder}
                />
                <Svg className="search__icon" path={search} width={24} />
            </div>
            {
                itemList && (
                    <ul className={searchListClasses}>
                        {
                            itemList.map(({ title, value }): ReactElement => (
                                <li
                                    className="search__list-item-label"
                                    key={title}
                                    onClick={(): void => handleChange({ title, value })}
                                >
                                    {title || value}
                                </li>
                            ))
                        }
                    </ul>
                )
            }
        </div>
    );
};

export default Search;
